import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  AlertTriangle, Info, AlertCircle, Bug,
  RefreshCw, Trash2, Search, ChevronDown, ChevronUp, X
} from 'lucide-react';

const NIVEL_CONFIG = {
  info:  { cor: 'bg-blue-50 text-blue-700 border-blue-200',   icone: <Info size={12}/>,          label: 'INFO'  },
  warn:  { cor: 'bg-yellow-50 text-yellow-700 border-yellow-200', icone: <AlertTriangle size={12}/>, label: 'WARN'  },
  error: { cor: 'bg-red-50 text-red-700 border-red-200',      icone: <AlertCircle size={12}/>,   label: 'ERRO'  },
  debug: { cor: 'bg-gray-50 text-gray-600 border-gray-200',   icone: <Bug size={12}/>,            label: 'DEBUG' },
};

function BadgeNivel({ nivel }) {
  const c = NIVEL_CONFIG[nivel] || NIVEL_CONFIG.info;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-xs font-bold ${c.cor}`}>
      {c.icone} {c.label}
    </span>
  );
}

function LogRow({ log }) {
  const [aberto, setAberto] = useState(false);
  const data = new Date(log.created_at);
  const dataStr = data.toLocaleDateString('pt-BR');
  const horaStr = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <>
      <tr
        className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${
          log.nivel === 'error' ? 'bg-red-50/30' : ''
        }`}
        onClick={() => setAberto(p => !p)}>
        <td className="px-3 py-2.5 whitespace-nowrap">
          <p className="text-xs text-gray-500">{dataStr}</p>
          <p className="text-xs font-mono text-gray-400">{horaStr}</p>
        </td>
        <td className="px-3 py-2.5"><BadgeNivel nivel={log.nivel} /></td>
        <td className="px-3 py-2.5">
          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">
            {log.categoria}
          </span>
        </td>
        <td className="px-3 py-2.5 max-w-xs">
          <p className="text-xs text-gray-700 truncate">{log.mensagem}</p>
        </td>
        <td className="px-3 py-2.5">
          {log.user_email
            ? <p className="text-xs text-gray-500 truncate max-w-[120px]">{log.user_email}</p>
            : <span className="text-xs text-gray-300">—</span>}
        </td>
        <td className="px-3 py-2.5 text-right">
          {log.dados
            ? aberto
              ? <ChevronUp size={13} className="text-gray-400 inline"/>
              : <ChevronDown size={13} className="text-gray-400 inline"/>
            : null}
        </td>
      </tr>
      {aberto && log.dados && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-4 py-3">
            <pre className="text-xs text-gray-600 font-mono bg-white border border-gray-100 rounded-lg p-3 overflow-x-auto max-h-40">
              {JSON.stringify(typeof log.dados === 'string' ? JSON.parse(log.dados) : log.dados, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminLogs() {
  const [logs,       setLogs]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [stats,      setStats]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filtroNivel, setFiltroNivel] = useState('');
  const [filtroCat,   setFiltroCat]   = useState('');
  const [busca,       setBusca]       = useState('');
  const [pagina,      setPagina]      = useState(0);
  const [limpando,    setLimpando]    = useState(false);
  const LIMIT = 100;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: pagina * LIMIT });
      if (filtroNivel) params.set('nivel', filtroNivel);
      if (filtroCat)   params.set('categoria', filtroCat);
      if (busca)       params.set('q', busca);
      const { data } = await api.get(`/admin/logs?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setStats(data.stats || []);
    } catch { toast.error('Erro ao carregar logs.'); }
    setLoading(false);
  }, [filtroNivel, filtroCat, busca, pagina]);

  useEffect(() => { carregar(); }, [carregar]);

  const limparLogs = async (dias) => {
    if (!window.confirm(`Remover logs com mais de ${dias} dias?`)) return;
    setLimpando(true);
    try {
      const { data } = await api.delete(`/admin/logs?dias=${dias}`);
      toast.success(`${data.removidos} logs removidos.`);
      carregar();
    } catch { toast.error('Erro ao limpar.'); }
    setLimpando(false);
  };

  const totalPorNivel = (n) => stats.find(s => s.nivel === n)?.total || 0;

  return (
    <Layout title="Logs do Sistema" subtitle="Registro de todos os eventos da plataforma">

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { n: 'info',  label: 'Info',   cor: 'text-blue-600',   bg: 'bg-blue-50' },
          { n: 'warn',  label: 'Avisos', cor: 'text-yellow-600', bg: 'bg-yellow-50' },
          { n: 'error', label: 'Erros',  cor: 'text-red-600',    bg: 'bg-red-50' },
          { n: 'debug', label: 'Debug',  cor: 'text-gray-500',   bg: 'bg-gray-50' },
        ].map(({ n, label, cor, bg }) => (
          <button key={n} onClick={() => setFiltroNivel(filtroNivel === n ? '' : n)}
            className={`card p-3 text-center border-2 transition-all ${filtroNivel === n ? 'border-acre-green' : 'border-transparent'}`}>
            <p className={`font-sora text-2xl font-bold ${cor}`}>{totalPorNivel(n)}</p>
            <p className={`text-xs font-semibold mt-0.5 ${cor}`}>{label}</p>
          </button>
        ))}
      </div>

      {/* Barra de filtros */}
      <div className="card p-3 mb-4 flex flex-wrap gap-2 items-center">
        {/* Busca */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-7 py-1.5 text-xs"
            placeholder="Buscar na mensagem..."
            value={busca}
            onChange={e => { setBusca(e.target.value); setPagina(0); }}
          />
          {busca && (
            <button onClick={() => setBusca('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={12}/>
            </button>
          )}
        </div>

        {/* Filtro categoria */}
        <input
          className="input py-1.5 text-xs w-40"
          placeholder="Categoria..."
          value={filtroCat}
          onChange={e => { setFiltroCat(e.target.value); setPagina(0); }}
        />

        {/* Filtro nível */}
        <select className="input py-1.5 text-xs w-32" value={filtroNivel} onChange={e => { setFiltroNivel(e.target.value); setPagina(0); }}>
          <option value="">Todos os níveis</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
          <option value="debug">Debug</option>
        </select>

        <button onClick={carregar} className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-acre-green hover:border-acre-green transition-colors" title="Atualizar">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>

        {/* Limpar logs */}
        <div className="relative group">
          <button disabled={limpando} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 text-xs font-semibold transition-colors disabled:opacity-50">
            <Trash2 size={12}/> Limpar
          </button>
          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-10 p-2 hidden group-hover:block min-w-[140px]">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => limparLogs(d)}
                className="w-full text-left text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">
                &gt; {d} dias
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Total */}
      <p className="text-xs text-gray-400 mb-2 px-1">{total.toLocaleString()} registros encontrados</p>

      {/* Tabela */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-4 border-acre-green border-t-transparent rounded-full animate-spin"/>
          </div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <p className="text-4xl mb-2">📋</p>
            <p className="text-sm">Nenhum log encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Data/Hora</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Nível</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Categoria</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Mensagem</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Usuário</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => <LogRow key={log.id} log={log} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginação */}
      {total > LIMIT && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button disabled={pagina === 0} onClick={() => setPagina(p => p - 1)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:border-acre-green transition-colors">
            ← Anterior
          </button>
          <span className="text-xs text-gray-500">
            Página {pagina + 1} de {Math.ceil(total / LIMIT)}
          </span>
          <button disabled={(pagina + 1) * LIMIT >= total} onClick={() => setPagina(p => p + 1)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:border-acre-green transition-colors">
            Próxima →
          </button>
        </div>
      )}
    </Layout>
  );
}
